import { Box, Button, useMediaQuery, useTheme } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { CustomLineProgress } from "components/LineProgress";
import { Observer } from "components/Observer";
import { ModalCount } from "pages/private/BatchCount/components/ModalCount";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  HiChevronDown,
  HiChevronRight,
  HiClock,
  HiExclamationCircle
} from "react-icons/hi";

import {
  BatchStatusCard,
  BatchStatusName,
  ClassBadge,
  DueBadge,
  DueCell,
  DueHeadCell,
  GroupRow,
  GroupChevronButton,
  GroupDueTable,
  GroupHeader,
  GroupHeaderLeft,
  GroupHeaderMeta,
  GroupListContainer,
  GroupMetaDot,
  GroupMetaText,
  GroupProductName,
  MobileBadgeRow,
  MobileCardHeader,
  MobileCardList,
  MobileMetaLine,
  MobileQtyBadge
} from "../../styles";
import { formatDueBadge } from "../../utils/dueStatus";
import { EditQuantitySheet } from "../EditQuantitySheet";

const PURCHASE_TYPE_UNIT = { 1: "Kg", 2: "Un" };

const formatAvailableQuantity = (value, purchaseType) => {
  const qty = Number(value ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const unit = PURCHASE_TYPE_UNIT[purchaseType];
  return unit ? `${qty} ${unit}` : qty;
};

const formatShelfLife = shelfLife => {
  if (shelfLife === null || shelfLife === undefined) {
    return "-";
  }
  return `${shelfLife} ${shelfLife === 1 ? "dia" : "dias"}`;
};

// Quantidade de lotes renderizados de uma vez dentro de um grupo aberto.
// Grupos com muitos lotes (ex.: 125) carregam aos poucos, sob demanda,
// pra nao travar a renderizacao quando o usuario expande o card.
const INITIAL_GROUP_ROWS = 20;
const GROUP_ROWS_STEP = 20;

// Agrupa os lotes por produto base. Os lotes ja chegam ordenados do mais
// urgente/vencido para o mais distante, entao o primeiro lote de cada grupo
// representa o "pior caso" (usado no resumo do cabecalho do grupo) e a ordem
// de insercao dos grupos ja fica do mais critico para o menos critico.
const groupBatchesByProduct = batches => {
  const groups = new Map();

  (batches || []).forEach(batch => {
    const key = batch.product?.id ?? batch.product?.name ?? "sem-produto";

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        productName: batch.product?.name || "Produto sem nome",
        batches: [],
        totalQuantity: 0,
        purchaseType: batch.purchase_type
      });
    }

    const group = groups.get(key);
    group.batches.push(batch);
    group.totalQuantity += Number(batch.available_quantity) || 0;
  });

  return Array.from(groups.values()).map(group => {
    const worstBatch = group.batches[0];
    return {
      ...group,
      worstDue: formatDueBadge(worstBatch?.minutes_to_due),
      worstClassification: worstBatch?.classification,
      worstShelfLife: worstBatch?.shelfLife
    };
  });
};

export const DashBatchTable = ({ batchData, loading, reload }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [visibleItems, setVisibleItems] = useState(5);
  const [visibleGroups, setVisibleGroups] = useState(5);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [visibleRowsByGroup, setVisibleRowsByGroup] = useState({});
  const [isModalCountOpen, setIsModalCountOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState({});

  const loadMoreItems = () => {
    setVisibleItems(prevVisibleItems => prevVisibleItems + 5);
  };

  const loadMoreGroups = () => {
    setVisibleGroups(prevVisibleGroups =>
      Math.min(prevVisibleGroups + 5, groups.length)
    );
  };

  const loadMoreGroupRows = key => {
    setVisibleRowsByGroup(prev => ({
      ...prev,
      [key]: (prev[key] ?? INITIAL_GROUP_ROWS) + GROUP_ROWS_STEP
    }));
  };

  const handleBatchQuantityCount = batch => {
    setSelectedBatch(batch);
    setIsModalCountOpen(true);
  };

  const handleOnCloseBatchQuantityCount = () => {
    setIsModalCountOpen(false);
  };

  const sortedBatches = useMemo(() => {
    const list = [...(batchData || [])];
    list.sort((a, b) => (a.minutes_to_due ?? 0) - (b.minutes_to_due ?? 0));
    return list;
  }, [batchData]);

  const groups = useMemo(
    () => groupBatchesByProduct(sortedBatches),
    [sortedBatches]
  );

  useEffect(() => {
    setVisibleItems(5);
    setVisibleGroups(5);
    // Todos os grupos comecam minimizados; o usuario expande o que quiser ver.
    setExpandedGroups({});
    setVisibleRowsByGroup({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchData]);

  const toggleGroup = key => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const visibleBatches = sortedBatches.slice(0, visibleItems);
  const visibleGroupsList = groups.slice(0, visibleGroups);

  return (
    <>
      {isMobile ? (
        <Box style={{ padding: "0.25rem" }}>
          {!!loading && <CustomLineProgress />}
          <MobileCardList>
            {visibleBatches?.map(batch => {
              const due = formatDueBadge(batch.minutes_to_due);
              const accent = due.overdue ? "#EB001B" : "#EC9C24";
              const StatusIcon = due.overdue ? HiExclamationCircle : HiClock;
              const badgeColors = due.overdue
                ? { background: "#FDECEE", color: "#C81E2B" }
                : { background: "#FFF6E6", color: "#B26A00" };
              const shelf =
                batch.shelfLife !== null && batch.shelfLife !== undefined
                  ? `${batch.shelfLife}d`
                  : "-";

              return (
                <BatchStatusCard
                  key={batch.id}
                  style={{ borderLeft: `4px solid ${accent}` }}
                >
                  <MobileCardHeader>
                    <Box
                      display="flex"
                      alignItems="center"
                      minWidth={0}
                      style={{ gap: "0.5rem" }}
                    >
                      <StatusIcon
                        size={18}
                        color={accent}
                        style={{ flexShrink: 0 }}
                      />
                      <BatchStatusName>{batch.product?.name}</BatchStatusName>
                    </Box>
                    <Button
                      aria-label={`Editar quantidade do lote ${batch.id}`}
                      style={{
                        color: "black",
                        minWidth: "auto",
                        padding: "0.25rem"
                      }}
                      onClick={() => handleBatchQuantityCount(batch)}
                    >
                      <EditIcon fontSize="small" />
                    </Button>
                  </MobileCardHeader>

                  <MobileBadgeRow>
                    <DueBadge style={badgeColors}>{due.text}</DueBadge>
                    <MobileQtyBadge>
                      Qtd. disp.{" "}
                      {formatAvailableQuantity(
                        batch.available_quantity,
                        batch.purchase_type
                      )}
                    </MobileQtyBadge>
                  </MobileBadgeRow>

                  <MobileMetaLine>
                    Lote {batch.id} • Classe {batch.classification} • Shelf{" "}
                    {shelf}
                  </MobileMetaLine>
                </BatchStatusCard>
              );
            })}
            <Observer callback={loadMoreItems} />
          </MobileCardList>
        </Box>
      ) : (
        <GroupListContainer>
          {!!loading && <CustomLineProgress />}
          {visibleGroupsList.map(group => {
            const due = group.worstDue;
            const badgeColors = due.overdue
              ? { background: "#FDECEE", color: "#C81E2B" }
              : { background: "#FFF6E6", color: "#B26A00" };
            const isOpen = !!expandedGroups[group.key];

            return (
              <GroupRow key={group.key}>
                <GroupHeader
                  onClick={() => toggleGroup(group.key)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? "Recolher" : "Expandir"} lotes de ${
                    group.productName
                  }`}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleGroup(group.key);
                    }
                  }}
                >
                  <GroupHeaderLeft>
                    <GroupChevronButton>
                      {isOpen ? (
                        <HiChevronDown size={16} />
                      ) : (
                        <HiChevronRight size={16} />
                      )}
                    </GroupChevronButton>
                    <DueBadge style={badgeColors}>{due.text}</DueBadge>
                    <GroupProductName>{group.productName}</GroupProductName>
                    {group.worstClassification ? (
                      <ClassBadge>{group.worstClassification}</ClassBadge>
                    ) : (
                      <GroupMetaText>
                        Shelf life {formatShelfLife(group.worstShelfLife)}
                      </GroupMetaText>
                    )}
                  </GroupHeaderLeft>

                  <GroupHeaderMeta>
                    <GroupMetaText>
                      {group.batches.length}{" "}
                      {group.batches.length === 1 ? "lote" : "lotes"}
                    </GroupMetaText>
                    <GroupMetaDot>•</GroupMetaDot>
                    <GroupMetaText>
                      {formatAvailableQuantity(
                        group.totalQuantity,
                        group.purchaseType
                      )}{" "}
                      perto do vencimento
                    </GroupMetaText>
                  </GroupHeaderMeta>
                </GroupHeader>

                {isOpen &&
                  (() => {
                    const rowsToShow =
                      visibleRowsByGroup[group.key] ?? INITIAL_GROUP_ROWS;
                    const visibleGroupBatches = group.batches.slice(
                      0,
                      rowsToShow
                    );
                    const hasMoreRows = group.batches.length > rowsToShow;

                    return (
                      <GroupDueTable>
                        <DueHeadCell>Vencimento</DueHeadCell>
                        <DueHeadCell>Lote</DueHeadCell>
                        <DueHeadCell style={{ justifyContent: "flex-end" }}>
                          Qtd. perto do vencimento
                        </DueHeadCell>
                        <DueHeadCell style={{ justifyContent: "center" }}>
                          Classe
                        </DueHeadCell>
                        <DueHeadCell>Shelf life</DueHeadCell>
                        <DueHeadCell style={{ justifyContent: "center" }}>
                          Ações
                        </DueHeadCell>

                        {visibleGroupBatches.map(batch => {
                          const batchDue = formatDueBadge(batch.minutes_to_due);
                          const batchBadgeColors = batchDue.overdue
                            ? { background: "#FDECEE", color: "#C81E2B" }
                            : { background: "#FFF6E6", color: "#B26A00" };

                          return (
                            <Fragment key={batch.id}>
                              <DueCell>
                                <DueBadge style={batchBadgeColors}>
                                  {batchDue.text}
                                </DueBadge>
                              </DueCell>
                              <DueCell
                                style={{ fontWeight: 700, color: "#111827" }}
                              >
                                {batch.id}
                              </DueCell>
                              <DueCell style={{ justifyContent: "flex-end" }}>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color: batchDue.overdue
                                      ? "#C81E2B"
                                      : "#111827"
                                  }}
                                >
                                  {formatAvailableQuantity(
                                    batch.available_quantity,
                                    batch.purchase_type
                                  )}
                                </span>
                              </DueCell>
                              <DueCell style={{ justifyContent: "center" }}>
                                <ClassBadge>{batch.classification}</ClassBadge>
                              </DueCell>
                              <DueCell>
                                {formatShelfLife(batch.shelfLife)}
                              </DueCell>
                              <DueCell style={{ justifyContent: "center" }}>
                                <Button
                                  aria-label={`Editar quantidade do lote ${batch.id}`}
                                  style={{
                                    minWidth: "auto",
                                    padding: "0.25rem",
                                    color: "#6B7280"
                                  }}
                                  onClick={() =>
                                    handleBatchQuantityCount(batch)
                                  }
                                >
                                  <EditIcon fontSize="small" />
                                </Button>
                              </DueCell>
                            </Fragment>
                          );
                        })}

                        {hasMoreRows && (
                          <Box style={{ gridColumn: "1 / -1" }}>
                            <Observer
                              callback={() => loadMoreGroupRows(group.key)}
                            />
                          </Box>
                        )}
                      </GroupDueTable>
                    );
                  })()}
              </GroupRow>
            );
          })}
          {visibleGroups < groups.length && (
            <Observer callback={loadMoreGroups} />
          )}
        </GroupListContainer>
      )}
      {isMobile ? (
        <EditQuantitySheet
          open={isModalCountOpen}
          onClose={handleOnCloseBatchQuantityCount}
          data={selectedBatch}
          reload={reload}
        />
      ) : (
        selectedBatch && (
          <ModalCount
            isOpen={isModalCountOpen}
            onBack={() => setIsModalCountOpen(false)}
            onClose={handleOnCloseBatchQuantityCount}
            data={selectedBatch}
            reload={reload}
          />
        )
      )}
    </>
  );
};
